import type { CameraView } from '@gozoki/api-types'
import { Autocomplete, TextField } from '@mui/material'
import { skipToken, useQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Edit, Plus } from 'lucide-react'
import { useMemo } from 'react'
import { GridLayout, noCompactor, useContainerWidth, type LayoutItem } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import z from 'zod/v3'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import { CameraSlotStream } from '~/components/routes/cameras/camera-views/camera-slot-stream'
import {
  GRID_MARGIN,
  GRID_PADDING,
  getVideoRowHeight,
} from '~/components/routes/cameras/camera-views/grid-aspect-ratio'
import { AuthorizedAppButton } from '~/components/new/buttons/authorized-button'
import {
  CAMERA_STREAM_ADD,
  CAMERA_STREAM_EDIT,
  CAMERA_STREAM_READ,
} from '~/components/routes/cameras/permissions'
import { withRoutePermissions } from '~/utils/rbac'
import { Spinner } from '~/components/ui/spinner'
import { api } from '~/lib/tuyau'

export const Route = createFileRoute('/_authenticated/cameras-views/')({
  staticData: {
    permission: CAMERA_STREAM_READ,
  },
  component: CameraViewsPage,
  pendingComponent: CameraViewsPagePending,
  validateSearch: z.object({
    viewId: z.string().optional(),
  }),
  loaderDeps: ({ search }) => ({ viewId: search.viewId }),
  loader: async ({ context: { queryClient }, deps: { viewId } }) => {
    const views = await queryClient.ensureQueryData(api.cameraViews.list.queryOptions({}))
    const activeViewId = views.find((v) => v.id === viewId)?.id ?? views[0]?.id ?? null
    if (activeViewId) {
      await queryClient.ensureQueryData(
        api.cameraViews.show.queryOptions({ params: { id: activeViewId } }),
      )
    }
  },
})
withRoutePermissions(Route)

function CameraViewsPagePending() {
  return (
    <>
      <AppHeader>
        <Title>Vues caméras</Title>
      </AppHeader>
      <AppContainer>
        <div className="flex items-center justify-center h-64 border rounded-lg">
          <Spinner className="size-8 text-[#6A9560]" />
        </div>
      </AppContainer>
    </>
  )
}

function CameraViewsPage() {
  const navigate = Route.useNavigate()
  const { viewId } = Route.useSearch()

  const { data: views = [] } = useQuery(api.cameraViews.list.queryOptions({}))

  const activeViewId = useMemo(
    () => views.find((v) => v.id === viewId)?.id ?? views[0]?.id ?? null,
    [views, viewId],
  )
  const activeViewQuery = useQuery(
    api.cameraViews.show.queryOptions(
      activeViewId === null ? skipToken : { params: { id: activeViewId } },
    ),
  )
  const activeView = activeViewQuery.data ?? null

  const viewOptions = views.map((v) => ({ label: v.name, value: v.id }))
  const selectedOption = activeView ? { label: activeView.name, value: activeView.id } : null

  const handleSelectView = (
    _: React.SyntheticEvent,
    option: { label: string; value: string } | null,
  ) => {
    navigate({ search: option ? { viewId: option.value } : {} })
  }

  return (
    <>
      <AppHeader>
        <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <Title>Vues caméras</Title>

          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            <div className="w-64">
              <Autocomplete
                size="small"
                options={viewOptions}
                value={selectedOption}
                disableClearable={true as boolean}
                onChange={handleSelectView}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                noOptionsText="Aucune vue"
                renderInput={(params) => <TextField {...params} label="Vue active" />}
              />
            </div>

            <AuthorizedAppButton
              permission={CAMERA_STREAM_EDIT}
              variant="outline"
              size="sm"
              disabled={activeView === null}
              render={
                <Link to="/cameras-views/$viewId/edit" params={{ viewId: activeView?.id ?? '' }} />
              }
            >
              <Edit className="size-4 mr-1" />
              Modifier
            </AuthorizedAppButton>

            <AuthorizedAppButton
              permission={CAMERA_STREAM_ADD}
              size="sm"
              render={<Link to="/cameras-views/new" />}
            >
              <Plus className="size-4 mr-1" />
              Nouvelle vue
            </AuthorizedAppButton>
          </div>
        </div>
      </AppHeader>

      <AppContainer>
        {activeView === null ? (
          <div className="flex items-center justify-center h-64 border rounded-lg text-muted-foreground text-sm">
            Aucune vue — créez votre première vue personnalisée
          </div>
        ) : (
          <DisplayGrid view={activeView} />
        )}
      </AppContainer>
    </>
  )
}

function DisplayGrid({ view }: { view: CameraView }) {
  const { width, containerRef } = useContainerWidth({ initialWidth: 800 })
  const rowHeight = getVideoRowHeight(width, view.cols)

  const layout: LayoutItem[] = view.slots.map((s) => ({
    i: s.id,
    x: s.x,
    y: s.y,
    w: s.w,
    h: s.h,
    static: true,
  }))

  return (
    <div
      ref={containerRef}
      className="border overflow-hidden bg-muted/30"
      style={{
        minHeight: view.rows * (rowHeight + GRID_MARGIN[1]) - GRID_MARGIN[1] + 2 * GRID_PADDING[1],
      }}
    >
      <GridLayout
        width={width}
        layout={layout}
        gridConfig={{
          cols: view.cols,
          rowHeight,
          margin: GRID_MARGIN,
          containerPadding: GRID_PADDING,
        }}
        dragConfig={{ enabled: false }}
        resizeConfig={{ enabled: false }}
        compactor={noCompactor}
      >
        {view.slots.map((slot) => (
          <div
            key={slot.id}
            className="bg-background border shadow-sm overflow-hidden flex flex-col"
          >
            <CameraSlotStream slot={slot} />
          </div>
        ))}
      </GridLayout>
    </div>
  )
}
